import { useEffect, useMemo, useState, useCallback, useContext, useRef } from "react";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import Button from "../components/Common/Button";
import Input from "../components/Common/Input";
import Pagination from "../components/Common/Pagination";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import ToastMessage from "../components/Common/ToastMessage";
import "../components/Common/UnitsListViewComponent.css";
import {
  createUser,
  deleteUser,
  getAuditTrailReportStatus,
  getUsers,
  requestAuditTrailReport,
  updateUser,
} from "../lib/api";
import CurrentUserContext from "../contexts/CurrentUserContext";
import { trackEvent } from "../lib/analytics";
import {
  AnalyticsSchemas,
  SearchContext,
  UserManagementAction,
  UserManagementScope,
} from "../lib/analyticsEvents";
import Label from "../components/Common/Label";

const PAGE_SIZE = 25;
const MAX_USER_NAME_LENGTH = 50;

const roleLabelMap = {
  FULL_ACCESS: "Super User",
  READ_WRITE: "Read-Write",
  READ_ONLY: "Read-Only",
};

const roleBadgeStyles = {
  FULL_ACCESS: "bg-[#F1E9FF] text-[#7A35D1] dark:bg-[#3A2A5C] dark:text-[#E1CCFF]",
  READ_WRITE: "bg-[#E6F0FF] text-[#1C4DBE] dark:bg-[#1F3566] dark:text-[#D7E6FF]",
  READ_ONLY: "bg-[#F1F2F4] text-[#5F6368] dark:bg-[#2E3A4A] dark:text-[#C7CED8]",
};

const formatDateMMDDYYYY = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const isValidEmail = (value = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getUserNameParts = (user) => {
  const firstName = user?.firstName?.trim() || "";
  const lastName = user?.lastName?.trim() || "";
  return { firstName, lastName };
};

const getUserInitials = (user) => {
  const { firstName, lastName } = getUserNameParts(user);
  const firstInitial = firstName[0] || "";
  const lastInitial = lastName[0] || "";
  const initials = `${firstInitial}${lastInitial}`.trim();
  if (initials) {
    return initials.toUpperCase();
  }
  return "--";
};

const getUserDisplayName = (user) => {
  const { firstName, lastName } = getUserNameParts(user);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  if (fullName) return fullName;
  return "--";
};

const truncateWithEllipsis = (value = "", maxLength = 50) => {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}...`;
};

const formatDateYYYYMMDD = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildAuditTrailPayload = (rangeKey, now = new Date()) => {
  if (rangeKey === "ALL") {
    return { startDate: null, endDate: null };
  }

  const rangeDaysByKey = {
    LAST_7: 7,
    LAST_30: 30,
    LAST_90: 90,
  };

  const rangeDays = rangeDaysByKey[rangeKey];
  if (!rangeDays) {
    return { startDate: null, endDate: null };
  }

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - (rangeDays - 1));
  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() + 1);

  return {
    startDate: formatDateYYYYMMDD(startDate),
    endDate: formatDateYYYYMMDD(endDate),
  };
};

export default function UserManagement() {
  const { currentUser } = useContext(CurrentUserContext);
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Read-Write");
  const [inviteError, setInviteError] = useState("");
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [auditRange, setAuditRange] = useState("LAST_30");
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [isInviteRoleOpen, setIsInviteRoleOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [updateUserId, setUpdateUserId] = useState(null);
  const [updateFirstName, setUpdateFirstName] = useState("");
  const [updateLastName, setUpdateLastName] = useState("");
  const [updateUserEmail, setUpdateUserEmail] = useState("");
  const [updateRole, setUpdateRole] = useState("Read-Write");
  const [updateOriginalFirstName, setUpdateOriginalFirstName] = useState("");
  const [updateOriginalLastName, setUpdateOriginalLastName] = useState("");
  const [updateOriginalRole, setUpdateOriginalRole] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [isUpdateRoleOpen, setIsUpdateRoleOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserEmail, setDeleteUserEmail] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [isAuditReportLoading, setIsAuditReportLoading] = useState(false);
  const [auditReportDownloadUrl, setAuditReportDownloadUrl] = useState("");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const auditReportTimeoutRef = useRef(null);
  const activeAuditReportIdRef = useRef(null);
  const [columnSizing, setColumnSizing] = useState(() => {
    try {
      const stored = localStorage.getItem("userManagementColumnWidths");
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  });

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
  }, []);

  const trackUserSearch = (text) => {
    const trimmedText = text.trim();
    if (!trimmedText) return;
    trackEvent(AnalyticsSchemas.webSearch, {
      search_context: SearchContext.userManagement,
      term_length: trimmedText.length,
    });
  };

  const rows = useMemo(
    () =>
      users.map((user) => ({
        ...user,
        fullName: getUserDisplayName(user),
        roleLabel: roleLabelMap[user.userAccessType] || user.userAccessType,
        added: formatDateMMDDYYYY(user.createdAt),
      })),
    [users]
  );

  const handleColumnSizingChange = useCallback((updater) => {
    setColumnSizing((prev) => {
      const updated = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem("userManagementColumnWidths", JSON.stringify(updated));
      } catch (error) {
        // ignore write failures
      }
      return updated;
    });
  }, []);

  const roleValueMap = useMemo(
    () => ({
      "Super User": "FULL_ACCESS",
      "Read-Write": "READ_WRITE",
      "Read-Only": "READ_ONLY",
    }),
    []
  );

  const roleKeyMap = useMemo(
    () => ({
      FULL_ACCESS: "Super User",
      READ_WRITE: "Read-Write",
      READ_ONLY: "Read-Only",
    }),
    []
  );

  const pollAuditTrailReportStatus = useCallback(async (reportId) => {
    try {
      const response = await getAuditTrailReportStatus(reportId);
      const reportData = response?.data?.data;

      if (activeAuditReportIdRef.current !== reportId) {
        return;
      }

      if (!reportData) {
        throw new Error("Invalid audit trail report status response");
      }

      if (reportData.status === "success") {
        activeAuditReportIdRef.current = null;
        setIsAuditReportLoading(false);
        setAuditReportDownloadUrl(reportData.downloadUrl || "");
        showToast("Audit trail generated successfully. Click Download to export the report.", "success");
        return;
      }

      if (reportData.status === "failed") {
        activeAuditReportIdRef.current = null;
        setIsAuditReportLoading(false);
        setAuditReportDownloadUrl("");
        showToast("Failed to generate audit trail report. Please try again.", "failed");
        return;
      }

      auditReportTimeoutRef.current = setTimeout(() => {
        pollAuditTrailReportStatus(reportId);
      }, 5000);
    } catch (error) {
      if (activeAuditReportIdRef.current !== reportId) {
        return;
      }
      activeAuditReportIdRef.current = null;
      setIsAuditReportLoading(false);
      setAuditReportDownloadUrl("");
      console.error("Failed to fetch audit trail report status:", error);
      showToast(error.message || "Failed to fetch audit trail report status.", "failed");
    }
  }, [showToast]);

  const handleDownloadAuditTrail = useCallback(async () => {
    const payload = buildAuditTrailPayload(auditRange);
    setIsAuditReportLoading(true);
    setAuditReportDownloadUrl("");

    if (auditReportTimeoutRef.current) {
      clearTimeout(auditReportTimeoutRef.current);
    }

    try {
      const response = await requestAuditTrailReport(payload);
      const reportId = response?.data?.data?.id;
      if (!reportId) {
        throw new Error("Missing audit trail report id");
      }

      activeAuditReportIdRef.current = reportId;
      pollAuditTrailReportStatus(reportId);
    } catch (error) {
      activeAuditReportIdRef.current = null;
      setIsAuditReportLoading(false);
      setAuditReportDownloadUrl("");
      console.error("Failed to request audit trail report:", error);
      showToast(error.message || "Failed to request audit trail report.", "failed");
    }
  }, [auditRange, pollAuditTrailReportStatus, showToast]);

  const resetAuditTrailState = useCallback(() => {
    activeAuditReportIdRef.current = null;
    setIsAuditReportLoading(false);
    setAuditReportDownloadUrl("");
    if (auditReportTimeoutRef.current) {
      clearTimeout(auditReportTimeoutRef.current);
      auditReportTimeoutRef.current = null;
    }
  }, []);

  const handleAuditTrailAction = useCallback(() => {
    if (auditReportDownloadUrl) {
      trackEvent(AnalyticsSchemas.webAuditTrailDownload, { date_range: auditRange });
      window.open(auditReportDownloadUrl);
      setAuditReportDownloadUrl("");
      return;
    }

    handleDownloadAuditTrail();
  }, [auditReportDownloadUrl, auditRange, handleDownloadAuditTrail]);

  useEffect(
    () => () => {
      activeAuditReportIdRef.current = null;
      if (auditReportTimeoutRef.current) {
        clearTimeout(auditReportTimeoutRef.current);
      }
    },
    []
  );

  const openUpdateModal = useCallback(
    (user) => {
      setUpdateUserId(user.unid);
      setUpdateFirstName(user.firstName || "");
      setUpdateLastName(user.lastName || "");
      setUpdateUserEmail(user.email);
      const roleLabel = roleKeyMap[user.userAccessType] || "Read-Write";
      setUpdateRole(roleLabel);
      setUpdateOriginalFirstName(user.firstName || "");
      setUpdateOriginalLastName(user.lastName || "");
      setUpdateOriginalRole(roleLabel);
      setUpdateError("");
      setIsUpdateOpen(true);
    },
    [roleKeyMap]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "fullName",
        header: <div className="text-left w-full">USER</div>,
        size: 280,
        cell: (info) => {
          const row = info.row.original;
          const initials = getUserInitials(row);
          const displayName = getUserDisplayName(row);
          const truncatedDisplayName = truncateWithEllipsis(displayName, 50);
          return (
            <div className="flex items-center gap-3">
              <span className="size-10 rounded-full bg-[#FF8C65] text-white text-sm font-semibold flex items-center justify-center">
                {initials}
              </span>
              <span className="text-sm text-dark-grey dark:text-white" title={displayName}>
                {truncatedDisplayName}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: <div className="text-left w-full">EMAIL</div>,
        size: 280,
        cell: (info) => (
          <span className="text-sm text-dark-grey dark:text-white text-left">
            {info.getValue()}
          </span>
        ),
      },
      {
        accessorKey: "roleLabel",
        header: <div className="text-center sm:text-left w-full">ROLE</div>,
        size: 180,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex justify-center sm:justify-start">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${roleBadgeStyles[row.userAccessType] || ""}`}>
                {row.roleLabel}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "added",
        header: <div className="text-center sm:text-left w-full">ADDED</div>,
        size: 140,
        cell: (info) => (
          <span className="text-sm text-dark-grey dark:text-white block text-center sm:text-left">
            {info.getValue()}
          </span>
        ),
      },
      {
        id: "actions",
        header: <div className="text-center w-full">ACTIONS</div>,
        size: 120,
        enableResizing: false,
        cell: (info) => {
          const row = info.row.original;
          const isOpen = activeActionMenu === row.unid;
          const isLocked = (currentUser?.email && row.email.toLowerCase() === currentUser.email.toLowerCase());
          return (
            <div className="flex items-center justify-center h-full relative" data-action-menu>
              <button
                className={`size-8 rounded-full flex items-center justify-center ${isLocked ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-white/10"
                  }`}
                onClick={() => {
                  if (isLocked) return;
                  setActiveActionMenu(isOpen ? null : row.unid);
                }}
                disabled={isLocked}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="4" r="1.5" fill="#5F6368" className="dark:fill-white" />
                  <circle cx="10" cy="10" r="1.5" fill="#5F6368" className="dark:fill-white" />
                  <circle cx="10" cy="16" r="1.5" fill="#5F6368" className="dark:fill-white" />
                </svg>
              </button>
              {isOpen && !isLocked && (
                <div className="absolute right-6 top-1/2 -translate-y-1/2 w-44 rounded-lg bg-white dark:bg-darkThemeModalBg shadow-appShadow border border-[#E6E8EC] dark:border-white/10 z-30">
                  <button
                    className="w-full px-3 py-2 flex items-center gap-2 text-sm text-dark-grey dark:text-white hover:bg-[#F8F9FB] dark:hover:bg-white/5 rounded-t-lg"
                    onClick={() => {
                      setActiveActionMenu(null);
                      openUpdateModal(row);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L20 6V12C20 16.42 16.42 20 12 22C7.58 20 4 16.42 4 12V6L12 2Z" stroke="#5F6368" strokeWidth="1.5" />
                      <path d="M9 12.5L11 14.5L15 10.5" stroke="#5F6368" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Edit User
                  </button>
                  <button
                    className="w-full px-3 py-2 flex items-center gap-2 text-sm text-red-500 hover:bg-[#F8F9FB] dark:hover:bg-white/5 rounded-b-lg"
                    onClick={() => {
                      setActiveActionMenu(null);
                      openDeleteModal(row);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M6 6L18 18" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    Delete User
                  </button>
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [activeActionMenu, currentUser?.email, openUpdateModal]
  );

  const gridHeight = useMemo(() => {
    const headerHeight = 50;
    const rowHeight = 64;
    const visibleRows = Math.max(rows.length, 1);
    const contentHeight = headerHeight + visibleRows * rowHeight;
    const minHeight = headerHeight + rowHeight;
    const maxHeight = 680;
    return Math.max(minHeight, Math.min(maxHeight, contentHeight));
  }, [rows.length]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnSizing },
    onColumnSizingChange: handleColumnSizingChange,
    columnResizeMode: "onChange",
    getRowId: (row) => row.unid || row.email,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("[data-action-menu]")) {
        setActiveActionMenu(null);
      }
      if (!event.target.closest("[data-invite-role]")) {
        setIsInviteRoleOpen(false);
      }
      if (!event.target.closest("[data-update-role]")) {
        setIsUpdateRoleOpen(false);
      }
      if (!event.target.closest("[data-delete-modal]")) {
        setIsDeleteOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (currentPage > pageCount - 1) {
      setCurrentPage(Math.max(0, pageCount - 1));
    }
  }, [currentPage, pageCount]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getUsers({
        pageNumber: currentPage + 1,
        pageSize: PAGE_SIZE,
        search: searchText.trim(),
      });
      const payload = response?.data?.data || {};
      setUsers(payload.users || []);
      setTotalCount(payload.count || 0);
    } catch (error) {
      setUsers([]);
      setTotalCount(0);
      showToast(error.message || "Failed to fetch users.", "failed");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchText, showToast]);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!isMounted) return;
      await fetchUsers();
    };
    run();
    return () => {
      isMounted = false;
    };
  }, [fetchUsers]);

  const trimmedUpdateFirstName = updateFirstName.trim();
  const trimmedUpdateLastName = updateLastName.trim();
  const isUpdateRoleUnchanged = updateRole === updateOriginalRole;
  const isUpdateNameUnchanged =
    trimmedUpdateFirstName === updateOriginalFirstName.trim() &&
    trimmedUpdateLastName === updateOriginalLastName.trim();
  const isUpdateUnchanged = isUpdateRoleUnchanged && isUpdateNameUnchanged;
  const isUpdateMissingRequired = !trimmedUpdateFirstName || !trimmedUpdateLastName;

  const closeInviteModal = () => {
    setInviteFirstName("");
    setInviteLastName("");
    setInviteEmail("");
    setInviteRole("Read-Write");
    setInviteError("");
    setIsInviteRoleOpen(false);
    setIsInviteOpen(false);
  };

  const handleInviteSubmit = async () => {
    setInviteError("");
    const trimmedFirstName = inviteFirstName.trim();
    const trimmedLastName = inviteLastName.trim();
    const trimmedEmail = inviteEmail.trim();
    if (!trimmedFirstName) {
      setInviteError("First name is required.");
      return;
    }
    if (!trimmedLastName) {
      setInviteError("Last name is required.");
      return;
    }
    if (trimmedFirstName.length > MAX_USER_NAME_LENGTH) {
      setInviteError("First name cannot exceed 50 characters.");
      return;
    }
    if (trimmedLastName.length > MAX_USER_NAME_LENGTH) {
      setInviteError("Last name cannot exceed 50 characters.");
      return;
    }
    if (!trimmedEmail) {
      setInviteError("Email is required.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setInviteError("Enter a valid email address.");
      return;
    }
    try {
      await createUser({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        email: trimmedEmail,
        userAccessType: roleValueMap[inviteRole] || "READ_WRITE",
      });
      trackEvent(AnalyticsSchemas.webUserManagement, {
        action: UserManagementAction.create,
        scope: UserManagementScope.selfService,
        target_user_unid: null,
      });
      closeInviteModal();
      showToast("User invited successfully.", "success");
      fetchUsers();
    } catch (error) {
      setInviteError(error.message || "Failed to invite user.");
      showToast(error.message || "Failed to invite user.", "failed");
    }
  };

  const handleUpdateSubmit = async () => {
    if (!updateUserId) return;
    setUpdateError("");
    if (!trimmedUpdateFirstName) {
      setUpdateError("First name is required.");
      return;
    }
    if (!trimmedUpdateLastName) {
      setUpdateError("Last name is required.");
      return;
    }
    if (trimmedUpdateFirstName.length > MAX_USER_NAME_LENGTH) {
      setUpdateError("First name cannot exceed 50 characters.");
      return;
    }
    if (trimmedUpdateLastName.length > MAX_USER_NAME_LENGTH) {
      setUpdateError("Last name cannot exceed 50 characters.");
      return;
    }
    if (isUpdateUnchanged) {
      setUpdateError("No changes to save.");
      return;
    }
    try {
      await updateUser(updateUserId, {
        firstName: trimmedUpdateFirstName,
        lastName: trimmedUpdateLastName,
        userAccessType: roleValueMap[updateRole] || "READ_WRITE",
      });
      setIsUpdateOpen(false);
      showToast("User updated successfully.", "success");
      fetchUsers();
    } catch (error) {
      setUpdateError(error.message || "Failed to update user.");
      showToast(error.message || "Failed to update user.", "failed");
    }
  };

  const openDeleteModal = (user) => {
    setDeleteUserId(user.unid);
    setDeleteUserEmail(user.email);
    setDeleteError("");
    setIsDeleteOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!deleteUserId) return;
    setDeleteError("");
    try {
      await deleteUser(deleteUserId);
      trackEvent(AnalyticsSchemas.webUserManagement, {
        action: UserManagementAction.delete,
        scope: UserManagementScope.selfService,
        target_user_unid: deleteUserId,
      });
      setIsDeleteOpen(false);
      showToast("User deleted successfully.", "success");
      fetchUsers();
    } catch (error) {
      setDeleteError(error.message || "Failed to delete user.");
      showToast(error.message || "Failed to delete user.", "failed");
    }
  };

  return (
    <>
      <div className="titleAndFilters">
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between items-start sm:items-center flex-wrap md:flex-nowrap">
          <div className="w-full text-center sm:text-left">
            <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]">
              User Management
            </h2>
            <p className="text-dark-grey dark:text-white/70 text-sm mt-2">
              Manage users and their access permissions
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h3
            className="text-[#000000DE] dark:text-white font-semibold text-base leading-5 shrink-0"
            data-testid="usermanagement-users-count"
          >
            Users {!isLoading && <span data-testid="usermanagement-users-count-value">({totalCount})</span>}
          </h3>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:ml-auto w-full sm:w-auto lg:w-auto">
            <div className="flex items-center gap-2 bg-white dark:bg-darkThemeModalBg shadow-appShadow rounded-md px-3 py-2 w-full sm:w-[280px]">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setSearchText(searchInput);
                    setCurrentPage(0);
                    trackUserSearch(searchInput);
                  }
                }}
                placeholder="Search users by name or email..."
                data-testid="usermanagement-search-input"
                className="bg-transparent w-full text-sm text-dark-grey dark:text-white outline-none placeholder:text-dark-grey/60 dark:placeholder:text-white/50"
              />
              <button
                type="button"
                onClick={() => {
                  setSearchText(searchInput);
                  setCurrentPage(0);
                  trackUserSearch(searchInput);
                }}
                className="shrink-0"
                aria-label="Search"
                data-testid="usermanagement-search-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="9.58342" cy="9.58366" r="7.91667" stroke="#1E2A2C" strokeWidth="1.5" className="dark:stroke-white" />
                  <path
                    d="M15.4167 15.417L18.3334 18.3337"
                    stroke="#1E2A2C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="dark:stroke-white"
                  />
                </svg>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center w-full sm:w-auto">
              <Button
                className="flex items-center justify-center gap-2 bg-[#344256] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#2B3646] transition w-full sm:w-auto"
                onClick={() => setIsAuditModalOpen(true)}
                data-testid="usermanagement-open-audit-modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3V14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M8.5 10.5L12 14L15.5 10.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 17.5C4 18.8807 5.11929 20 6.5 20H17.5C18.8807 20 20 18.8807 20 17.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Download Audit Trial
              </Button>
              <Button
                className="flex items-center justify-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto"
                onClick={() => setIsInviteOpen(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12C14.2091 12 16 10.2091 16 8Z" stroke="white" strokeWidth="1.5" />
                  <path d="M4 20C4 16.6863 6.68629 14 10 14H14C17.3137 14 20 16.6863 20 20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M19 8V12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M17 10H21" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Invite User
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-2 unitsListHolder rounded-md scrollVisible overflow-x-auto w-full">
          <div className="units-data-grid" style={{ height: gridHeight, width: "100%" }}>
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} style={{ height: 50 }}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="units-table-header-cell"
                        style={{ width: header.getSize() }}
                      >
                        <div className="units-table-header-content text-center sm:text-left w-full">
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                        {header.column.getCanResize() && (
                          <div
                            className={`column-resizer ${header.column.getIsResizing() ? "is-resizing" : ""}`}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              header.getResizeHandler()(event);
                            }}
                            onTouchStart={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              header.getResizeHandler()(event);
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading ? (
                  <tr className="units-table-row" style={{ height: 64 }}>
                    <td className="units-table-cell" colSpan={columns.length}>
                      <span className="text-sm text-dark-grey dark:text-white">Loading users…</span>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="units-table-row" style={{ height: 64 }}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="units-table-cell"
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            onPageChange={({ selected }) => setCurrentPage(selected)}
          />
        )}
      </div>

      {isInviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow">
            <div className="flex items-center justify-between border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">Invite New User</h3>
              <button
                className="text-dark-grey/70 dark:text-white/70 hover:text-dark-grey dark:hover:text-white"
                onClick={closeInviteModal}
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">First Name</Label>
                <Input
                  type="text"
                  value={inviteFirstName}
                  maxLength={MAX_USER_NAME_LENGTH}
                  onChange={(e) => {
                    setInviteFirstName(e.target.value);
                    if (inviteError) {
                      setInviteError("");
                    }
                  }}
                  placeholder="First Name"
                  className="w-full rounded-md border border-[#E6E8EC] dark:border-[#0B2E57] bg-white dark:bg-[#0F3B7C] px-3 py-2 text-sm text-dark-grey dark:text-white placeholder:text-dark-grey/60 dark:placeholder:text-white/60"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">Last Name</Label>
                <Input
                  type="text"
                  value={inviteLastName}
                  maxLength={MAX_USER_NAME_LENGTH}
                  onChange={(e) => {
                    setInviteLastName(e.target.value);
                    if (inviteError) {
                      setInviteError("");
                    }
                  }}
                  placeholder="Last Name"
                  className="w-full rounded-md border border-[#E6E8EC] dark:border-[#0B2E57] bg-white dark:bg-[#0F3B7C] px-3 py-2 text-sm text-dark-grey dark:text-white placeholder:text-dark-grey/60 dark:placeholder:text-white/60"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">Email Address</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    if (inviteError) {
                      setInviteError("");
                    }
                  }}
                  placeholder="john@example.com"
                  className="w-full rounded-md border border-[#E6E8EC] dark:border-[#0B2E57] bg-white dark:bg-[#0F3B7C] px-3 py-2 text-sm text-dark-grey dark:text-white placeholder:text-dark-grey/60 dark:placeholder:text-white/60"
                />
                <p className="text-xs text-dark-grey/70 dark:text-white/60">
                  A password reset email will be sent to this address.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">Role</Label>
                <div className="relative" data-invite-role>
                  <button
                    type="button"
                    onClick={() => setIsInviteRoleOpen((prev) => !prev)}
                    className="w-full rounded-md border border-[#AFC3BC] bg-white dark:darkThemeGradiantBg px-3 py-2 text-sm text-dark-grey dark:text-white shadow-sm flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#FF8C65]/40"
                  >
                    <span>{inviteRole}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M19.92 8.95L13.4 15.47C12.63 16.24 11.37 16.24 10.6 15.47L4.08 8.95" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {isInviteRoleOpen && (
                    <div className="absolute left-0 right-0 mt-2 rounded-md border border-[#D1D5DB] bg-white dark:bg-darkThemeDropdownBg dark:border-darkThemeBorder dark:shadow-darkThemeDropdownMenuShadow shadow-lg z-30 overflow-hidden">
                      {["Read-Write", "Read-Only", "Super User"].map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => {
                            setInviteRole(role);
                            setIsInviteRoleOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${inviteRole === role
                              ? "bg-[#1E66D0] text-white"
                              : "text-dark-grey dark:text-white hover:bg-[#F3F4F6] dark:hover:bg-darkThemeDropdownItemBg"
                            }`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-dark-grey/70 dark:text-white/60">
                  {inviteRole === "Read-Write" && "Can view and modify all resources, except for account users"}
                  {inviteRole === "Read-Only" && "Can view all resources"}
                  {inviteRole === "Super User" && "Full access to all resources, including account users"}
                </p>
              </div>
              {inviteError && (
                <p className="text-sm text-red-500">{inviteError}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                className="bg-white text-dark-grey border border-[#E6E8EC] dark:border-[#FF8C65] dark:text-white dark:bg-transparent px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                onClick={closeInviteModal}
              >
                Cancel
              </Button>
              <Button
                className="flex items-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleInviteSubmit}
                disabled={
                  !inviteFirstName.trim() ||
                  !inviteLastName.trim() ||
                  !inviteEmail.trim() ||
                  !isValidEmail(inviteEmail)
                }
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M22 2L11 13" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Send Invite
              </Button>
            </div>
          </div>
        </div>
      )}

      {isUpdateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow">
            <div className="flex items-center justify-between border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">Edit User</h3>
              <button
                className="text-dark-grey/70 dark:text-white/70 hover:text-dark-grey dark:hover:text-white"
                onClick={() => setIsUpdateOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">First Name</Label>
                <Input
                  type="text"
                  value={updateFirstName}
                  maxLength={MAX_USER_NAME_LENGTH}
                  onChange={(e) => {
                    setUpdateFirstName(e.target.value);
                    if (updateError) {
                      setUpdateError("");
                    }
                  }}
                  placeholder="First Name"
                  className="w-full rounded-md border border-[#E6E8EC] dark:border-[#0B2E57] bg-white dark:bg-[#0F3B7C] px-3 py-2 text-sm text-dark-grey dark:text-white placeholder:text-dark-grey/60 dark:placeholder:text-white/60"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">Last Name</Label>
                <Input
                  type="text"
                  value={updateLastName}
                  maxLength={MAX_USER_NAME_LENGTH}
                  onChange={(e) => {
                    setUpdateLastName(e.target.value);
                    if (updateError) {
                      setUpdateError("");
                    }
                  }}
                  placeholder="Last Name"
                  className="w-full rounded-md border border-[#E6E8EC] dark:border-[#0B2E57] bg-white dark:bg-[#0F3B7C] px-3 py-2 text-sm text-dark-grey dark:text-white placeholder:text-dark-grey/60 dark:placeholder:text-white/60"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">Email Address</Label>
                <div className="w-full rounded-md border border-[#E6E8EC] dark:border-[#0B2E57] bg-[#F3F4F6] dark:bg-[#0F3B7C] px-3 py-2 text-sm text-dark-grey dark:text-white">
                  {updateUserEmail || "--"}
                </div>
                <p className="text-xs text-dark-grey/70 dark:text-white/60">
                  Email cannot be updated for existing users.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white">Role</Label>
                <div className="relative" data-update-role>
                  <button
                    type="button"
                    onClick={() => setIsUpdateRoleOpen((prev) => !prev)}
                    className="w-full rounded-md border border-[#AFC3BC] bg-white dark:darkThemeGradiantBg px-3 py-2 text-sm text-dark-grey dark:text-white shadow-sm flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#FF8C65]/40"
                  >
                    <span>{updateRole}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M19.92 8.95L13.4 15.47C12.63 16.24 11.37 16.24 10.6 15.47L4.08 8.95" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {isUpdateRoleOpen && (
                    <div className="absolute left-0 right-0 mt-2 rounded-md border border-[#D1D5DB] bg-white dark:bg-darkThemeDropdownBg dark:border-darkThemeBorder dark:shadow-darkThemeDropdownMenuShadow shadow-lg z-30 overflow-hidden">
                      {["Read-Write", "Read-Only", "Super User"].map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => {
                            setUpdateRole(role);
                            setIsUpdateRoleOpen(false);
                            if (updateError) {
                              setUpdateError("");
                            }
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${updateRole === role
                              ? "bg-[#1E66D0] text-white"
                              : "text-dark-grey dark:text-white hover:bg-[#F3F4F6] dark:hover:bg-darkThemeDropdownItemBg"
                            }`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-dark-grey/70 dark:text-white/60">
                  {updateRole === "Read-Write" && "Can view and modify all resources"}
                  {updateRole === "Read-Only" && "Can view all resources"}
                  {updateRole === "Super User" && "Full access to all resources"}
                </p>
              </div>
              {updateError && (
                <p className="text-sm text-red-500">{updateError}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                className="bg-white text-dark-grey border border-[#E6E8EC] dark:border-[#FF8C65] dark:text-white dark:bg-transparent px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                onClick={() => setIsUpdateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex items-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleUpdateSubmit}
                disabled={isUpdateUnchanged || isUpdateMissingRequired}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {isDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow" data-delete-modal>
            <div className="flex items-center justify-between border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">Delete User</h3>
              <button
                className="text-dark-grey/70 dark:text-white/70 hover:text-dark-grey dark:hover:text-white"
                onClick={() => setIsDeleteOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-dark-grey dark:text-white">
                Are you sure you want to delete this user?
              </p>
              <div className="rounded-md border border-[#E6E8EC] dark:border-white/10 bg-[#F8F9FB] dark:bg-white/10 px-3 py-2 text-sm text-dark-grey dark:text-white">
                {deleteUserEmail || "--"}
              </div>
              {deleteError && <p className="text-sm text-red-500">{deleteError}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                className="bg-white text-dark-grey border border-[#E6E8EC] dark:border-[#FF8C65] dark:text-white dark:bg-transparent px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                onClick={() => setIsDeleteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex items-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition"
                onClick={handleDeleteSubmit}
              >
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}

      {isAuditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow">
            <div className="flex items-center justify-between border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">Download Audit Trail</h3>
              <button
                className="text-dark-grey/70 dark:text-white/70 hover:text-dark-grey dark:hover:text-white"
                onClick={() => {
                  setIsAuditModalOpen(false);
                  resetAuditTrailState();
                }}
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-dark-grey/70 dark:text-white/70 font-semibold">
                Select Time Frame
              </p>
              <div className="space-y-3">
                {[
                  { key: "LAST_7", title: "Last 7 Days", desc: "Recent activity from the past week" },
                  { key: "LAST_30", title: "Last 30 Days", desc: "Activity from the past month" },
                  { key: "LAST_90", title: "Last 90 Days", desc: "Quarterly activity report" },
                  { key: "ALL", title: "All Time", desc: "Complete audit history" },
                ].map((option) => {
                  const isSelected = auditRange === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => {
                        setAuditRange(option.key);
                        resetAuditTrailState();
                      }}
                      data-testid={`usermanagement-audit-range-${option.key}`}
                      className={`w-full text-left rounded-xl border px-4 py-3 flex items-start gap-3 ${isSelected
                          ? "border-[#FF8C65] bg-[#FFF5EC] dark:bg-[#3B241B]"
                          : "border-[#E6E8EC] dark:border-white/10 bg-white dark:bg-darkThemeModalBg"
                        }`}
                    >
                      <span
                        className={`mt-1 size-4 rounded-full border flex items-center justify-center ${isSelected ? "border-[#FF8C65]" : "border-[#C9CED6] dark:border-white/30"
                          }`}
                      >
                        {isSelected && <span className="size-4 rounded-full bg-[#FF8C65]" />}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-dark-grey dark:text-white">
                          {option.title}
                        </span>
                        <span className="block text-xs text-dark-grey/70 dark:text-white/60">
                          {option.desc}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="rounded-xl border border-[#FF8C65]/60 bg-[#FFF5EC] dark:bg-[#3B241B] px-4 py-3 flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M8 3V6" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M16 3V6" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M4 9H20" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                  <rect x="4" y="5" width="16" height="16" rx="3" stroke="#FF8C65" strokeWidth="1.5" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-[#FF8C65]">
                    {auditRange === "LAST_7" && "Last 7 Days Selected"}
                    {auditRange === "LAST_30" && "Last 30 Days Selected"}
                    {auditRange === "LAST_90" && "Last 90 Days Selected"}
                    {auditRange === "ALL" && "All Time Selected"}
                  </p>
                  <p className="text-xs text-[#FF8C65]/90">
                    The audit trail will be exported as a CSV file containing all activity logs for the selected time period.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                className="bg-white text-dark-grey border border-[#E6E8EC] dark:border-[#FF8C65] dark:text-white dark:bg-transparent px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                onClick={() => {
                  setIsAuditModalOpen(false);
                  resetAuditTrailState();
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex items-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleAuditTrailAction}
                disabled={isAuditReportLoading}
                data-testid="usermanagement-audit-action-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3V14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M8.5 10.5L12 14L15.5 10.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 17.5C4 18.8807 5.11929 20 6.5 20H17.5C18.8807 20 20 18.8807 20 17.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                {isAuditReportLoading ? (
                  <>
                    <LoadingSpinner size="small" className="text-white fill-white" />
                    Generating...
                  </>
                ) : auditReportDownloadUrl ? (
                  "Download"
                ) : (
                  "Generate Audit Trail"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ToastMessage
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />
    </>
  );
}
